import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";
import { Reveal } from "@/components/site/Reveal";

export const Testimonials = () => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const count = TESTIMONIALS.length;

  const paginate = useCallback((d) => {
    setDir(d);
    setIdx((p) => (p + d + count) % count);
  }, [count]);

  useEffect(() => {
    const t = setInterval(() => paginate(1), 6000);
    return () => clearInterval(t);
  }, [paginate]);

  const t = TESTIMONIALS[idx];

  return (
    <section id="testimonials" data-testid="testimonials-section" className="bg-lightgray py-24 sm:py-32">
      <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
        <Reveal>
          <span className="text-brick font-bold text-sm tracking-[0.18em] uppercase">Reviews</span>
          <h2 className="mt-3 font-heading font-extrabold tracking-tight text-charcoal text-4xl sm:text-5xl lg:text-6xl">
            What homeowners say.
          </h2>
        </Reveal>

        <div className="mt-12 relative min-h-[340px] sm:min-h-[300px]">
          <Quote className="w-14 h-14 text-brick/15 mx-auto mb-6" />
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={idx}
              data-testid="testimonial-card"
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(t.rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-brick fill-brick" />
                ))}
              </div>
              <p className="font-heading text-charcoal text-xl sm:text-2xl lg:text-3xl leading-snug font-medium max-w-3xl mx-auto">
                “{t.quote}”
              </p>
              <div className="mt-8 flex flex-col items-center justify-center">
                <p className="font-heading font-bold text-charcoal">{t.name}</p>
                <p className="text-charcoal/50 text-sm">{t.location}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-4">
          <button
            data-testid="testimonial-prev"
            onClick={() => paginate(-1)}
            className="w-11 h-11 rounded-full bg-white border border-black/5 shadow-sm flex items-center justify-center hover:bg-brick hover:text-white text-charcoal transition-colors"
            aria-label="Previous review"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex gap-2">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => { setDir(i > idx ? 1 : -1); setIdx(i); }}
                className={`h-2 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-brick" : "w-2 bg-charcoal/20"}`}
                aria-label={`Go to review ${i + 1}`}
              />
            ))}
          </div>
          <button
            data-testid="testimonial-next"
            onClick={() => paginate(1)}
            className="w-11 h-11 rounded-full bg-white border border-black/5 shadow-sm flex items-center justify-center hover:bg-brick hover:text-white text-charcoal transition-colors"
            aria-label="Next review"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};
